import React, { useId, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
type DisclosureProps = {
  /** Small uppercase eyebrow shown above the trigger label, e.g. a section index. */
  eyebrow?: string;
  label: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
};

/**
 * Hairline disclosure row. The panel content stays mounted in the DOM at all
 * times — only its height is clipped — so search engines and AI answer engines
 * still read the copy while the page reads short and quiet.
 */
export function Disclosure({
  eyebrow,
  label,
  children,
  defaultOpen = false
}: DisclosureProps) {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();
  const reduceMotion = useReducedMotion();
  return <div className="border-b border-line" data-id="element-23" data-mp="314x105x7qe15">
      <h3 data-id="element-24" data-mp="23ohr45cvbt03">
        <button type="button" onClick={() => setOpen(value => !value)} aria-expanded={open} aria-controls={panelId} className="group flex w-full items-baseline gap-5 py-6 text-left md:gap-8" data-id="element-25" data-mp="23ori9w9ng67d">
          {eyebrow && <span className={`w-8 shrink-0 text-[0.6rem] uppercase tracking-micro transition-colors ${open ? 'text-lilac' : 'text-muted group-hover:text-lilac'}`} data-id="element-26" data-mp="32ulhvn9pnjjv">
              {eyebrow}
            </span>}
          <span className={`flex-1 text-sm leading-snug transition-colors md:text-base ${open ? 'text-cream' : 'text-cream/75 group-hover:text-cream'}`} data-id="element-27" data-mp="2t2foc93t8u2k">
            {label}
          </span>
          <span aria-hidden="true" className="relative mt-1 h-3 w-3 shrink-0 self-start" data-id="element-28" data-mp="2f3z9tvzphz9v">
            <span className={`absolute left-0 top-1/2 h-px w-3 -translate-y-1/2 transition-colors ${open ? 'bg-lilac' : 'bg-muted group-hover:bg-lilac'}`} data-id="element-29" data-mp="2a6zcc9gxa4ud" />
            <span className={`absolute left-1/2 top-0 h-3 w-px -translate-x-1/2 transition-[transform,background-color] duration-200 ease-out ${open ? 'scale-y-0 bg-lilac' : 'bg-muted group-hover:bg-lilac'}`} data-id="element-30" data-mp="8f2rg98v7imx" />
          </span>
        </button>
      </h3>

      <motion.div id={panelId} initial={false} animate={{
      height: open ? 'auto' : 0,
      opacity: open ? 1 : 0
    }} transition={{
      duration: reduceMotion ? 0 : 0.28,
      ease: [0.23, 1, 0.32, 1]
    }} className="overflow-hidden" data-id="element-31" data-mp="ig56vzx8r0lx">
        <div className="pb-8 md:pl-[3.25rem]" data-id="element-32" data-mp="1f6ef69o8nkne">{children}</div>
      </motion.div>
    </div>;
}